import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../redux/hook'
import {
    getEntityTableFiltersIndex,
    getEntityTableFilters,
} from '../utils/EntityUtils'
import {
    IColumn,
    IFilterValue,
    IStatusFilter,
    ITableFilters,
} from '../components/Entity/interfaces'
import { setFilterList } from '../redux/slices/entitySlice'
import { useTranslation } from 'react-i18next'
import { parsePreSelectedShipmentStatuses } from '../utils/IntransitUtils'

export const useEntityInstanceFiltersInit = (
    templateId: string,
    applicationId: string | undefined,
    tabIndex: string,
    isInTransitMapView: boolean,
): void => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)
    const allTableFilters = useAppSelector(
        (state) => state.entity.allTableFilters,
    )
    const columnVisibilityList = useAppSelector(
        (state) => state.entity.entityColumns,
    )
    const columnVisibilityTemplateId = useAppSelector(
        (state) => state.entity.columnVisibilityTemplateId,
    )
    const columnVisibilityOrgId = useAppSelector(
        (state) => state.entity.columnVisibilityOrgId,
    )
    const intransitSlice = useAppSelector((state) => state.inTransit)

    const currentTableFiltersIndex = getEntityTableFiltersIndex(
        allTableFilters,
        templateId,
        applicationId,
        tabIndex,
    )

    const allAttributesFilters: Record<string, IFilterValue> =
        getEntityTableFilters(
            allTableFilters,
            templateId,
            applicationId,
            tabIndex,
        )

    useEffect(() => {
        if (
            columnVisibilityTemplateId !== templateId ||
            columnVisibilityOrgId !== (org?.id || '')
        ) {
            return
        }

        const isFiltersSetAlready = currentTableFiltersIndex !== -1

        if (!columnVisibilityList?.length) {
            return
        }

        const searchableColumnsList: IColumn[] =
            columnVisibilityList
                ?.filter((column) => column?.searchable)
                .map((i) => {
                    return {
                        ...i,
                        columnName: t(i.columnName),
                    } as unknown as IColumn
                }) ?? []

        const initialSelectedColumn =
            searchableColumnsList?.find(
                (column) => column?.attributeName === 'device',
            ) ??
            searchableColumnsList?.[0] ??
            undefined

        if (isFiltersSetAlready) {
            const currentFilter = allTableFilters[currentTableFiltersIndex]

            if (
                currentFilter.gloalSearchText?.column === undefined &&
                initialSelectedColumn
            ) {
                const updatedAllTableFilters = allTableFilters.map(
                    (filter, index) => {
                        if (index === currentTableFiltersIndex) {
                            return {
                                ...filter,
                                gloalSearchText: {
                                    ...filter.gloalSearchText,
                                    column: initialSelectedColumn,
                                },
                            }
                        }
                        return filter
                    },
                )

                dispatch(setFilterList(updatedAllTableFilters))
            }

            return
        }

        let currentPageFilters: ITableFilters = {
            applicationId,
            tabIndex,
            entityTemplateId: templateId,
            allAttributesFilters: {},
            gloalSearchText: {
                column: initialSelectedColumn,
                searchText: '',
                type: 'globalSearchText',
            },
            entityStatusFilter: {
                type: 'status',
                values: [],
            },
        }

        let shipmentStatusFilters = [] as string[]
        const shipmentStatusAttributeId =
            intransitSlice.inTransitSettings.shipmentStatusAttributeId ??
            'shipmentStatus'
        if (isInTransitMapView) {
            const preSelectedShipmentStatuses: string[] =
                parsePreSelectedShipmentStatuses(
                    intransitSlice.inTransitSettings
                        .preSelectedShipmentStatuses,
                )
            shipmentStatusFilters = preSelectedShipmentStatuses
            currentPageFilters = {
                ...currentPageFilters,
                allAttributesFilters: {
                    ...allAttributesFilters,
                    [shipmentStatusAttributeId]: {
                        type: 'status',
                        isColumnLevelFilter: false,
                        values: shipmentStatusFilters,
                    } as IStatusFilter,
                },
            }
        }
        dispatch(setFilterList([...allTableFilters, currentPageFilters]))
    }, [
        templateId,
        applicationId,
        tabIndex,
        isInTransitMapView,
        allTableFilters,
        dispatch,
        intransitSlice,
        currentTableFiltersIndex,
        allAttributesFilters,
        columnVisibilityList,
        columnVisibilityTemplateId,
        columnVisibilityOrgId,
        org?.id,
        t,
    ])
}

export default useEntityInstanceFiltersInit
